import { motion, AnimatePresence } from 'framer-motion'
import { Upload, ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useState } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { FloatingActionIslandContent } from '@/components/ui/FloatingActionIslandContent'

interface DataTableColumn {
  header: string
  cell: (item: any) => React.ReactNode
  width?: string
  minWidth?: string
  className?: string
}

interface DataTableProps {
  data: any[]
  columns: DataTableColumn[]
  itemType: string
  isDragActive: boolean
  dropzoneProps: any
  inputProps?: React.InputHTMLAttributes<HTMLInputElement> & { refKey?: string }
}

export function DataTable({
  data,
  columns,
  itemType,
  isDragActive,
  dropzoneProps,
  inputProps,
}: DataTableProps) {
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 50

  const totalPages = Math.ceil(data.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const paginatedData = data.slice(startIndex, endIndex)

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1)
    }
  }

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1)
    }
  }

  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page)
    }
  }

  return (
    <div {...dropzoneProps} className="relative">
      {inputProps && <input {...inputProps} />}

      <div className="space-y-4">
        <div
          className={cn(
            'rounded-lg border overflow-hidden relative transition-all duration-300 ease-out min-h-96',
            isDragActive
              ? 'border-success bg-success-subtle shadow-lg'
              : 'border-border',
          )}
        >
          <AnimatePresence>
            {isDragActive && (
              <>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                  className="absolute inset-0 rounded-lg z-10"
                  style={{
                    background: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)',
                    pointerEvents: 'none',
                  }}
                />

                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                  className="absolute inset-0 flex justify-center rounded-lg z-20"
                  style={{
                    pointerEvents: 'none',
                    alignItems: 'flex-start',
                    paddingTop: '4rem',
                  }}
                >
                  <motion.div
                    initial={{ y: 20, opacity: 0, scale: 0.8 }}
                    animate={{ y: 0, opacity: 1, scale: 1 }}
                    exit={{ y: -20, opacity: 0, scale: 0.8 }}
                    transition={{
                      type: 'spring',
                      stiffness: 600,
                      damping: 30,
                      delay: 0.1,
                    }}
                    className="bg-card p-6 rounded-xl shadow-xl border border-success-border"
                  >
                    <motion.div
                      initial={{ y: -5, rotate: -5 }}
                      animate={{ y: 0, rotate: 0 }}
                      transition={{
                        type: 'spring',
                        stiffness: 400,
                        damping: 25,
                        delay: 0.2,
                      }}
                    >
                      <Upload className="h-8 w-8 text-success mx-auto mb-3" />
                    </motion.div>
                    <motion.p
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.2 }}
                      className="text-base font-semibold text-success-emphasis"
                    >
                      Drop CSV file to upload {itemType}
                    </motion.p>
                  </motion.div>
                </motion.div>
              </>
            )}
          </AnimatePresence>

          <motion.div
            animate={
              isDragActive
                ? { scale: 0.95, opacity: 0.3 }
                : { scale: 1, opacity: 1 }
            }
            transition={{ duration: 0.2 }}
          >
            <Table>
              <TableHeader>
                <TableRow>
                  {columns.map((column, index) => (
                    <TableHead
                      key={index}
                      style={{ width: column.width, minWidth: column.minWidth }}
                      className="text-xs font-medium text-muted-foreground uppercase tracking-wider"
                    >
                      {column.header}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedData.map(item => (
                  <TableRow key={item.id}>
                    {columns.map((column, index) => (
                      <TableCell
                        key={index}
                        className={cn(
                          'text-sm text-muted-foreground',
                          column.className || 'whitespace-nowrap',
                        )}
                      >
                        {column.cell(item)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </motion.div>
        </div>
      </div>

      {/* Floating Action Island Content */}
      <FloatingActionIslandContent
        show={totalPages > 1}
        componentId="data-table-pagination"
        priority={10}
      >
        <div className="inline-flex w-full lg:w-[520px] max-w-2xl">
          <div className="flex-grow items-center space-x-2 text-sm text-muted-foreground whitespace-nowrap pointer-events-none hidden md:flex">
            <span className="font-medium">
              <span className="mr-1">
                {data.length} {itemType}
              </span>
              <span className="text-muted-foreground/60 mx-1 ">•</span>
              <span className="ml-1">
                {startIndex + 1}-{Math.min(endIndex, data.length)} of{' '}
                {data.length}
              </span>
            </span>
          </div>

          <div className="flex items-center space-x-2 w-[240px] justify-center md:justify-end pointer-events-auto">
            <div className="inline-flex items-center space-x-1">
              <button
                onClick={goToPreviousPage}
                disabled={currentPage === 1}
                className="pointer-events-auto relative inline-flex items-center justify-center w-10 h-10 text-sm font-medium text-muted-foreground bg-background/80 border border-border rounded-lg hover:bg-muted hover:border-border disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>

              {/* Compact page display for floating island */}
              <div className="inline-flex items-center space-x-1 justify-end">
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  let pageNumber
                  if (totalPages <= 5) {
                    pageNumber = i + 1
                  } else if (currentPage <= 3) {
                    pageNumber = i + 1
                  } else if (currentPage >= totalPages - 2) {
                    pageNumber = totalPages - 4 + i
                  } else {
                    pageNumber = currentPage - 2 + i
                  }

                  return (
                    <button
                      key={pageNumber}
                      onClick={() => goToPage(pageNumber)}
                      className={cn(
                        'pointer-events-auto relative inline-flex items-center justify-center w-10 h-10 text-sm font-medium border rounded-lg transition-all',
                        pageNumber === currentPage
                          ? 'z-10 bg-black/90 text-white border-black'
                          : 'bg-background/80 border-border text-muted-foreground hover:bg-muted hover:border-border',
                      )}
                    >
                      {pageNumber}
                    </button>
                  )
                })}
              </div>

              <button
                onClick={goToNextPage}
                disabled={currentPage === totalPages}
                className="pointer-events-auto relative inline-flex items-center justify-center w-10 h-10 text-sm font-medium text-muted-foreground bg-background/80 border border-border rounded-lg hover:bg-muted hover:border-border disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </FloatingActionIslandContent>
    </div>
  )
}
